import { FC, Fragment } from 'react';
import styled from 'styled-components';
import { mediaBreakpointUp, paddingY } from '@/styles/layout/utils';
import { Container } from '@/components/layout/Container';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import Link from '@/components/Link';
import { ArrowLeftGlyph } from '@/components/icons/ArrowLeftGlyph';
import gtm from '@/lib/gtm';
import { COLORS } from '@/styles/color';

export interface LinkGridProps {
  groups: {
    heading: string;
    cta?: {
      text: string;
      href: string | URL;
    };
    links: {
      title: string;
      description: string;
      href: string;
    }[];
  }[];
}

const LinkGrid: FC<LinkGridProps> = ({ groups }) => {
  return (
    <Section>
      <StyledContainer>
        {groups.map((group) => (
          <Fragment key={group.heading}>
            <Heading>{group.heading}</Heading>

            {group.cta && (
              <Link href={group.cta.href}>
                <CTA
                  onClick={() => {
                    gtm.track('click', {
                      element_label: group.cta!.text,
                      element_type: 'link',
                      location: 'Feature Blocks',
                    });
                  }}
                >
                  {group.cta.text} <ArrowLeftGlyph width={20} />
                </CTA>
              </Link>
            )}

            <List>
              {group.links.map((link) => (
                <li key={`${group.heading}_${link.title}`}>
                  <Link
                    href={link.href}
                    onClick={() => {
                      gtm.track('click', {
                        element_type: 'link',
                        element_label: link.title,
                        location: 'Link Grid',
                      });
                    }}
                  >
                    <div>
                      <h6>
                        <span>{link.title}</span>
                        <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M6.336 0.736515L11.072 5.45652L6.352 10.1765L5.184 9.00852L8.72 5.45652L7.776 4.51252H9.712V6.41652H0.528V4.51252H7.776L5.168 1.90452L6.336 0.736515Z"
                            fill={COLORS.WHITE}
                          />
                        </svg>
                      </h6>
                      <p>{link.description}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </List>
          </Fragment>
        ))}
      </StyledContainer>
    </Section>
  );
};

export default LinkGrid;

// Styled Components

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(60)};
  }
`;

const Heading = styled.h3`
  ${customTextSize(24, 30)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(30)};

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: ${remCalc(40)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 38)};
    margin-bottom: 0;
  }
`;

const CTA = styled.a`
  display: inline-flex;
  align-items: center;
  ${customTextSize(16, 20)};
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-top: ${remCalc(-30)};
  margin-bottom: ${remCalc(60)};

  svg {
    transform: rotate(180deg);
    margin-left: 0.25rem;
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: 0;
  }
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${remCalc(30)};

  li {
    cursor: pointer;
    ${customTextSize(14, 20)};

    h6 {
      ${customTextSize(16, 20)};
      font-weight: ${FONT_WEIGHTS.BOLD};
      margin-bottom: ${remCalc(10)};
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;

      svg {
        display: inline-block;
        margin-left: ${remCalc(10)};
      }
    }
  }

  ${mediaBreakpointUp('tablet')} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${remCalc(30, 80)};
  }

  ${mediaBreakpointUp('desktop')} {
    gap: ${remCalc(60, 130)};

    li {
      ${customTextSize(16, 24)};

      h6 {
        ${customTextSize(24, 34)};
        font-weight: ${FONT_WEIGHTS.SEMIBOLD};

        svg {
          width: 18px;
          height: auto;
        }
      }
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    column-gap: ${remCalc(156)};
  }
`;

const Section = styled.section`
  background-image: linear-gradient(209deg, ${COLORS.GRAY.LOWLIGHT} 23.25%, ${COLORS.BLUE.LOWLIGHT} 161.08%);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  color: ${COLORS.WHITE};

  ${paddingY(60)};

  ${Heading}:first-of-type,
  ${List}:first-of-type {
    margin-bottom: ${remCalc(60)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(120)};

    ${Heading}:first-of-type,
    ${List}:first-of-type {
      margin-bottom: inherit;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${Heading}:first-of-type {
      ${customTextSize(49, 58)};
    }
  }
`;
